// LLM API utility functions
import {getApiBase} from '../config/apiBase';
import {fetchWithAwpHeaders} from './awpFetch';

/**
 * Send a request to the LLM API
 * @param {Array} messages - Array of message objects with role and content
 * @param {Object} options - Optional configuration for the LLM
 * @returns {Promise<Object>} The LLM response
 */
export async function callLLM(messages, model, options = {}) {

    const requestBody = {
        messages,
        model,
        options
    };
    console.log('Calling LLM with request:', requestBody);

    const llmUrl = `${getApiBase()}/llm`;

    try {
        const response = await fetchWithAwpHeaders(llmUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`LLM API request failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('LLM API error:', error);
        throw error;
    }
}

/**
 * Request updated node from LLM with improved text while preserving marks
 * @param {Object} task - The task object with suggestedText and instructions
 * @param {Object} nodeData - The current node data with marks
 * @returns {Promise<Object>} Updated node data from LLM
 */
export async function getUpdatedNodeFromLLM(task, nodeData) {
    // Build the system prompt
    const systemPrompt = `You are an expert text editor that improves document text while preserving all formatting and structure.

You will receive a ProseMirror node structure in JSON format. Your task is to apply the suggested text changes while keeping all formatting marks intact.

CRITICAL RULES:
1. Return the COMPLETE node structure - the entire JSON object with all fields
2. ONLY modify the "text" field values (the actual characters/letters)
3. PRESERVE EXACTLY as-is:
   - "type" fields (e.g., "paragraph", "text")
   - "attrs" objects and all their properties  
   - "marks" arrays and all mark objects with their types and attrs
   - The overall structure
4. When the suggested text is different length, intelligently redistribute text across existing nodes while keeping their marks
5. Return ONLY the complete valid JSON - no explanations, no markdown code blocks, no partial responses
6. The response must be the FULL node object, parseable by JSON.parse()

Example - if given this node:
{
  "type": "paragraph",
  "attrs": { "blockId": "abc123" },
  "content": [
    { "type": "text", "text": "The quick ", "marks": [] },
    { "type": "text", "text": "brown fox", "marks": [{ "type": "bold", "attrs": {} }] },
    { "type": "text", "text": " jumps.", "marks": [] }
  ],
  "marks": []
}

And suggested text: "A fast red fox leaps."

You must return the COMPLETE updated node:
{
  "type": "paragraph",
  "attrs": { "blockId": "abc123" },
  "content": [
    { "type": "text", "text": "A fast ", "marks": [] },
    { "type": "text", "text": "red fox", "marks": [{ "type": "bold", "attrs": {} }] },
    { "type": "text", "text": " leaps.", "marks": [] }
  ],
  "marks": []
}`;


    const userPrompt = `${JSON.stringify({task, nodeData})}`;

    const messages = [
        {role: 'system', content: systemPrompt},
        {role: 'user', content: userPrompt}
    ];

    const model = 'gpt-5.1-codex';

    const options = {
        temperature: 0.3, // Lower temperature for more consistent formatting
        max_tokens: 3000
    };

    try {
        const response = await callLLM(messages, model, options);

        // Extract the updated node from the response
        // Assuming the response has a 'choices' array with the LLM's text response
        const content = response.choices?.[0]?.message?.content || response.content;

        if (!content) {
            throw new Error('No content in LLM response');
        }

        // Try to parse the JSON from the response
        // Handle case where LLM might wrap it in markdown code blocks
        let jsonStr = content.trim();
        if (jsonStr.startsWith('```')) {
            jsonStr = jsonStr.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
        }

        return JSON.parse(jsonStr);
    } catch (error) {
        console.error('Error getting updated node from LLM:', error);
        throw error;
    }
}
